import { useMemo, useState } from 'react';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  fetchPipelineHealth,
  fetchRunDetail,
  fetchRuns,
  retryRun,
  startRun
} from '$lib/api/adminScraper';
import type { ScrapeRunDetail, ScrapeRunSummary } from '$lib/api/adminScraper';
import { DashboardHeader } from '$lib/components/admin/DashboardHeader';
import { PortalCardsRow } from '$lib/components/admin/PortalCardsRow';
import { RecentRunsTable } from '$lib/components/admin/RecentRunsTable';
import { DivergencePanel } from '$lib/components/admin/DivergencePanel';
import { ApiDownBanner } from '$lib/components/admin/ApiDownBanner';
import { fetchDivergenceSignals } from '$lib/api/adminScraper';
import { useDashboardPolling } from '$lib/hooks/useDashboardPolling';

export function AdminDashboardPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [windowDays, setWindowDays] = useState<5 | 30>(5);

  const healthQuery = useQuery({
    queryKey: ['admin', 'pipeline-health'],
    queryFn: fetchPipelineHealth,
    // Slightly tighter staleTime than the global default so the cards feel
    // live when the operator just clicked "Start scrape" and a status is
    // about to flip to running.
    staleTime: 5_000
  });

  const runsQuery = useQuery({
    queryKey: ['admin', 'runs', { days: windowDays }],
    queryFn: () => fetchRuns({ days: windowDays, page_size: 50 }),
    staleTime: 5_000
  });

  const signalsQuery = useQuery({
    queryKey: ['admin', 'divergence-signals'],
    queryFn: () => fetchDivergenceSignals(10),
    // Divergence events arrive after the enrich stage — they don't need to
    // be hammered every poll tick.
    staleTime: 30_000
  });

  // Detail fetch per active run. We key by portal slug so the cards can pick
  // their own run without the dashboard threading per-card promises.
  const activeRunSummaries = useMemo<ScrapeRunSummary[]>(() => {
    if (!runsQuery.data) return [];
    // Latest run per portal (the runs list is already created_at desc).
    const seen = new Set<string>();
    const latest: ScrapeRunSummary[] = [];
    for (const r of runsQuery.data.items) {
      if (seen.has(r.portal_slug)) continue;
      seen.add(r.portal_slug);
      latest.push(r);
    }
    return latest.filter((r) => r.status === 'running' || r.status === 'pending');
  }, [runsQuery.data]);

  const detailQueries = useQueries({
    queries: activeRunSummaries.map((run) => ({
      queryKey: ['admin', 'run-detail', run.id],
      queryFn: () => fetchRunDetail(run.id),
      staleTime: 5_000
    }))
  });

  const activeRunsByPortal = useMemo(() => {
    const map: Record<string, ScrapeRunDetail | null> = {};
    detailQueries.forEach((q, i) => {
      const summary = activeRunSummaries[i];
      if (!summary) return;
      map[summary.portal_slug] = q.data ?? null;
    });
    return map;
  }, [detailQueries, activeRunSummaries]);

  const isAnyRunActive = activeRunSummaries.length > 0;

  const polling = useDashboardPolling({ isAnyRunActive });

  // Wire react-query refetchInterval through after the queries are declared
  // so we don't reach for state inside the queryFn closure.
  // useQuery accepts refetchInterval as an option — the cleanest way to
  // change cadence based on derived state is to set it via setQueryDefaults.
  // Here we instead trigger refetches manually via setInterval-like polling
  // by setting refetchInterval at query option level using polling.refetchInterval.
  // (See note: we can't conditionally pass `refetchInterval` after the hooks
  // have been called without re-rendering — react-query reads the option per
  // render so changing the polling state automatically takes effect.)

  // Re-declare the queries with refetch options. We didn't above to avoid
  // React-rules-of-hooks duplication; instead we rely on `enabled: visible`
  // inside the hook output and keep refetch via window focus.
  // To actually drive polling we attach refetch on a side-effect interval.
  // (Effect lives in the polling hook so visibilitychange is centralised.)

  // Drive polling: when the dashboard polling state says we should be polling,
  // invalidate the relevant queries on cadence. Centralising this here means
  // the hook stays read-only and each query re-mounts cleanly when the user
  // toggles the windowDays.
  useQueryPolling(polling.refetchInterval, () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'pipeline-health'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'runs'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'run-detail'] });
  });

  const apiDown = healthQuery.isError;
  const lastHealthSuccess = healthQuery.dataUpdatedAt ? new Date(healthQuery.dataUpdatedAt).toISOString() : null;

  // First-ever empty: health resolved with zero portals AND there are no runs
  // in the window. We deliberately don't check `runsQuery` alone because the
  // 5-day window can be empty in steady state — the empty *page* needs both
  // sources to agree.
  const portals = healthQuery.data?.portals ?? [];
  const isFirstEverEmpty =
    !healthQuery.isPending &&
    !runsQuery.isPending &&
    portals.length === 0 &&
    (runsQuery.data?.items.length ?? 0) === 0;

  if (isFirstEverEmpty) {
    return (
      <div className="flex flex-1 flex-col">
        <header className="flex shrink-0 items-center gap-3 border-b border-rule bg-surface-2 px-4 py-3">
          <h1 className="text-md font-semibold text-fg">{t('admin.dashboard.title')}</h1>
        </header>
        <div className="flex flex-1 items-center justify-center p-12" data-testid="admin-empty">
          <div className="max-w-md text-center">
            <h2 className="text-md font-semibold text-fg">{t('admin.empty.title')}</h2>
            <p className="mt-1 text-xs text-fg-muted">{t('admin.empty.subtitle')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto">
      {apiDown && <ApiDownBanner lastSuccessAt={lastHealthSuccess} />}

      <DashboardHeader
        portals={portals}
        isPolling={polling.isPolling}
        pollingIntervalSeconds={polling.intervalSeconds}
        onStartScrape={async (slug) => {
          await startRun(slug);
          queryClient.invalidateQueries({ queryKey: ['admin'] });
        }}
      />

      <PortalCardsRow
        portals={portals}
        activeRunsByPortal={activeRunsByPortal}
        loading={healthQuery.isPending}
        onStartFreshRun={async (slug) => {
          await startRun(slug);
          queryClient.invalidateQueries({ queryKey: ['admin'] });
        }}
        onRetry={async (slug) => {
          // Retry targets the most recent failed run for the portal — backend
          // exposes /runs/{id}/retry, so we look it up from the runs list
          // first. Falling back to startRun avoids leaving the operator
          // stranded if the failed run drops out of the window.
          const target = (runsQuery.data?.items ?? []).find(
            (r) => r.portal_slug === slug && r.status === 'failed'
          );
          if (target) await retryRun(target.id);
          else await startRun(slug);
          queryClient.invalidateQueries({ queryKey: ['admin'] });
        }}
      />

      <RecentRunsTable
        runs={runsQuery.data?.items ?? []}
        portals={portals}
        loading={runsQuery.isPending}
        windowDays={windowDays}
        onWindowChange={setWindowDays}
      />

      <DivergencePanel signals={signalsQuery.data?.items ?? []} loading={signalsQuery.isPending} />
    </div>
  );
}

// Lightweight setInterval wrapper that respects the dashboard polling state.
// Lives inside the page module so the polling hook can stay pure.
import { useEffect } from 'react';
function useQueryPolling(intervalMs: number | false, tick: () => void) {
  useEffect(() => {
    if (intervalMs === false) return;
    const id = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(id);
    // tick is recreated each render; the dashboard re-binds the interval when
    // polling cadence changes, which is rare (only when isAnyRunActive flips).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs]);
}
